import { useLayoutEffect, useRef, type RefObject } from "react";

const rowStart = "--hud-row-start";
const rowSpan = "--hud-row-span";
const column = "--hud-column";

// Keep source order and card state intact while supporting panels fill
// the shorter desktop lane. Screen CSS owns row size and compact fallback.
export function useStaggeredGrid(): RefObject<HTMLDivElement> {
  const gridRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    let cards: HTMLElement[] = [];
    let frame = 0;
    let width = grid.getBoundingClientRect().width;

    const clearPlacement = (card: HTMLElement) => {
      card.style.removeProperty(rowStart);
      card.style.removeProperty(rowSpan);
      card.style.removeProperty(column);
    };

    const measure = () => {
      const styles = getComputedStyle(grid);
      const unit = parseFloat(styles.getPropertyValue("--hud-row-unit"));
      if (!Number.isFinite(unit) || unit <= 0) {
        cards.forEach(clearPlacement);
        return;
      }

      const gap = parseFloat(styles.getPropertyValue("--hud-lane-gap")) || 0;
      const gapRows = Math.ceil(Math.max(0, gap) / unit);
      const nextRow = { primary: 1, secondary: 1 };

      // A panel's lane depends only on preceding panels. Measure after choosing
      // its width to avoid stale heights; this small fixed panel list bounds
      // sequential layout reads. Apply all row placement together afterward.
      const placements = cards.map((card) => {
        const requestedLane = card.dataset.hudLane;
        const lane: keyof typeof nextRow = requestedLane === "auto"
          ? nextRow.primary <= nextRow.secondary ? "primary" : "secondary"
          : requestedLane === "secondary" ? "secondary" : "primary";
        const chosenColumn = lane === "primary" ? "1" : "2";
        if (card.style.getPropertyValue(column) !== chosenColumn) card.style.setProperty(column, chosenColumn);
        const span = Math.max(1, Math.ceil(card.getBoundingClientRect().height / unit));
        const start = nextRow[lane];
        nextRow[lane] += span + gapRows;
        return { card, start: String(start), span: String(span) };
      });

      placements.forEach(({ card, start, span }) => {
        if (card.style.getPropertyValue(rowStart) !== start) card.style.setProperty(rowStart, start);
        if (card.style.getPropertyValue(rowSpan) !== span) card.style.setProperty(rowSpan, span);
      });
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    const resizeObserver = new ResizeObserver((entries) => {
      let changed = false;
      for (const entry of entries) {
        if (entry.target !== grid) {
          changed = true;
          continue;
        }
        const nextWidth = grid.getBoundingClientRect().width;
        if (nextWidth !== width) {
          width = nextWidth;
          changed = true;
        }
      }
      // Our row placement changes the container height; only its width
      // requires another measurement.
      if (changed) schedule();
    });

    const attachCards = () => {
      const nextCards = Array.from(grid.children).filter((child): child is HTMLElement =>
        child instanceof HTMLElement && (child.dataset.hudLane === "primary" || child.dataset.hudLane === "secondary" || child.dataset.hudLane === "auto"),
      );
      for (const card of cards) {
        if (!nextCards.includes(card)) {
          resizeObserver.unobserve(card);
          clearPlacement(card);
        }
      }
      for (const card of nextCards) {
        if (!cards.includes(card)) resizeObserver.observe(card, { box: "border-box" });
      }
      cards = nextCards;
    };

    attachCards();
    measure();
    resizeObserver.observe(grid, { box: "border-box" });

    const mutationObserver = new MutationObserver(() => {
      attachCards();
      schedule();
    });
    mutationObserver.observe(grid, { childList: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (frame) cancelAnimationFrame(frame);
      cards.forEach(clearPlacement);
    };
  }, []);

  return gridRef;
}
